import clsx from "clsx";
import { AnimatePresence, motion } from "motion/react";
import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { Button } from "../components/buttons/Button";
import { Xmark } from "../components/icons/Icons";
import { isEmpty } from "../helpers";
import { Tooltip } from "react-tooltip";
import { TranslationProvider, useTranslation } from "../i18n";

export type ModalProps = PropsWithChildren<{
  title?: string;
  confirm?: () => void;
  backgroundClose?: boolean;
  className?: string;
  hideCloseButton?: boolean;
  onClose?: () => void;
  /**
   * Stay open when the account scope changes (see GlobalModalProvider): for
   * flows that change the account themselves, such as connecting a wallet or
   * signing in, and for dialogs that follow the current account on their own.
   */
  keepOnAccountChange?: boolean;
}>;

type GlobalModalContext = {
  showModal: (modalProps: ModalProps) => void;
  showModalIfClosed: (modalProps: ModalProps) => boolean;
  isCurrentModal: (modalProps: ModalProps) => boolean;
  hideModal: () => void;
};

const initialState: GlobalModalContext = {
  showModal: () => {},
  showModalIfClosed: () => false,
  isCurrentModal: () => false,
  hideModal: () => {},
};

type ModalState = {
  modalProps: ModalProps;
  /** The account scope the open dialog belongs to. */
  openedScope?: string;
  /** The account scope the app acts for now. */
  accountScope?: string;
};

const GlobalModalContext = createContext(initialState);
const GlobalModalStateContext = createContext<ModalState>({ modalProps: {} });
export const useGlobalModalContext = () => useContext(GlobalModalContext);
export const useGlobalModalOpen = () =>
  !isEmpty(useContext(GlobalModalStateContext).modalProps);

const ModalContent: FC<{
  modalProps: ModalProps;
  openedScope?: string;
  hideModal: () => void;
}> = ({ modalProps, openedScope, hideModal }) => {
  const { t } = useTranslation("common");
  // The current scope comes from context, not props: a closing dialog keeps
  // its last props while it fades out and must not act for the next account.
  const { accountScope } = useContext(GlobalModalStateContext);
  const accountChanged =
    !modalProps.keepOnAccountChange && openedScope !== accountScope;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      // Motion 11 uses its JavaScript animation path when onUpdate is present.
      // Keep opacity in inline styles so native animation cancellation cannot
      // expose the open modal again while React completes its removal.
      onUpdate={() => {}}
      className={clsx({
        modal: true,
        [`${modalProps.className}`]: modalProps.className,
      })}
      onClick={() => (modalProps.backgroundClose ? hideModal() : null)}>
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.9 }}
        className="modal__window"
        onClick={(e) => {
          e.stopPropagation();
        }}>
        {modalProps.hideCloseButton !== true && (
          <button
            className="transparent modal__close fs-12 color-white fw-600 flex ai-c mr-1 mb-1 hover-primary1"
            onClick={hideModal}>
            <Xmark className="w-2 h-a mr-1" />
            {t("close")}
          </button>
        )}
        {modalProps.title && (
          <div className="modal__header">
            <h2>{modalProps.title}</h2>
          </div>
        )}
        {!accountChanged && modalProps.children && modalProps.children}
        {!accountChanged && modalProps.confirm && (
          <div className="modal__footer mt-4 px-3 py-2 text-right">
            <Button
              className="button--grey button--outline"
              onClick={hideModal}
              label={t("cancel")}
            />
            <Button
              className="button ml-1"
              onClick={modalProps.confirm}
              label={t("confirm")}
            />
          </div>
        )}
        <Tooltip id="modal-tip" className="tooltip" />
      </motion.div>
    </motion.div>
  );
};

// Own modal state above session/data providers so callers keep one stable
// controller even when an account query suspends or changes its render branch.
//
// accountScope names the account the app acts for. A dialog belongs to the
// scope it opened in and closes as soon as that changes, so a dialog opened
// for one account never acts for another (opt out: keepOnAccountChange).
export const GlobalModalProvider: FC<{
  children: React.ReactNode;
  accountScope?: string;
}> = ({ children, accountScope }) => {
  const [modalProps, setModalProps] = useState<ModalProps>({});
  const currentModal = useRef<ModalProps>({});
  // Taken in the render that first shows a dialog, not when showModal runs:
  // a click that selects an account and opens its dialog binds it to that one.
  const [opened, setOpened] = useState({ modalProps, scope: accountScope });
  if (opened.modalProps !== modalProps)
    setOpened({ modalProps, scope: accountScope });
  const openedScope =
    opened.modalProps === modalProps ? opened.scope : accountScope;
  const accountChanged =
    !isEmpty(modalProps) &&
    !modalProps.keepOnAccountChange &&
    openedScope !== accountScope;

  const showModal = useCallback((modalProps: ModalProps) => {
    currentModal.current = modalProps;
    document.body.style.overflow = "hidden";
    setModalProps(modalProps);
  }, []);

  const hideModal = useCallback(() => {
    currentModal.current = {};
    document.body.style.overflow = "";
    setModalProps({});
  }, []);

  const isCurrentModal = useCallback(
    (expected: ModalProps) => currentModal.current === expected,
    []
  );

  // Background notices must not replace consent or an in-progress product
  // dialog, including two requests made before React commits either update.
  const showModalIfClosed = useCallback(
    (next: ModalProps) => {
      if (!isEmpty(currentModal.current)) return false;
      showModal(next);
      return true;
    },
    [showModal]
  );

  // Runs after the effects below it, so an owner that already replaced or
  // closed its dialog for the new account keeps that; otherwise the owner
  // hears of the close through onClose, as when the user closes it.
  useEffect(() => {
    if (!accountChanged || !isCurrentModal(modalProps)) return;
    modalProps.onClose?.();
    hideModal();
  }, [accountChanged, hideModal, isCurrentModal, modalProps]);

  const controls = useMemo(
    () => ({ showModal, showModalIfClosed, isCurrentModal, hideModal }),
    [showModal, showModalIfClosed, isCurrentModal, hideModal]
  );
  const state = useMemo(
    () => ({ modalProps, openedScope, accountScope }),
    [modalProps, openedScope, accountScope]
  );

  return (
    <GlobalModalContext.Provider value={controls}>
      <GlobalModalStateContext.Provider value={state}>
        {children}
      </GlobalModalStateContext.Provider>
    </GlobalModalContext.Provider>
  );
};

// Render exactly one outlet below the contexts needed by modal children, but
// outside data-loading Suspense branches. Portals retain this outlet's context.
export const GlobalModalOutlet: FC = () => {
  const { modalProps, openedScope } = useContext(GlobalModalStateContext);
  const { hideModal } = useGlobalModalContext();

  // Closing from the dialog itself (close button, backdrop) notifies its owner,
  // as an account change does; a programmatic hideModal() does not.
  const closeModal = useCallback(() => {
    modalProps.onClose?.();
    hideModal();
  }, [hideModal, modalProps]);

  const dest =
    typeof document !== "undefined" ? document.getElementById("modal") : null;

  const renderComponent = () => {
    return dest
      ? ReactDOM.createPortal(
          <TranslationProvider namespace="common">
            <AnimatePresence>
              {!isEmpty(modalProps) && (
                <ModalContent
                  modalProps={modalProps}
                  openedScope={openedScope}
                  hideModal={closeModal}
                />
              )}
            </AnimatePresence>
          </TranslationProvider>,
          dest
        )
      : null;
  };

  return renderComponent();
};

// Preserve the existing standalone API for consumers with a stable tree.
export const GlobalModal: FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <GlobalModalProvider>
    {children}
    <GlobalModalOutlet />
  </GlobalModalProvider>
);
